import { captureException } from '@platform/observability';
import { _notifyConsentChange, setTrackingConsent } from './context.js';
import { getOrCreateDataLayer } from './dataLayer.js';
import { flushQueue } from './track.js';
import type { ConsentProvider, TrackingConfig } from './types.js';

let config: TrackingConfig | null = null;
let cleanupConsentSubscription: (() => void) | null = null;

export function initTracking(cfg: TrackingConfig): () => void {
  if (config) {
    cfg.logger?.debug('[tracking] initTracking() re-initializing (HMR/StrictMode).');
    cleanupConsentSubscription?.();
    cleanupConsentSubscription = null;
  }
  config = cfg;
  if (cfg.gtmContainerId && !document.getElementById('gtm-script')) {
    loadGTM(cfg.gtmContainerId, cfg.gtmHost, cfg.gtmDataLayer);
  }

  if (cfg.consentProvider) {
    initConsent(cfg.consentProvider);
  }
  flushQueue();

  return () => {
    cleanupConsentSubscription?.();
  };
}

function initConsent(provider: ConsentProvider) {
  const consent = provider.getConsent();
  if (consent !== null) {
    setTrackingConsent(consent);
  }
  if (provider.subscribe) {
    try {
      cleanupConsentSubscription = provider.subscribe(() => {
        const next = provider.getConsent();
        if (next !== null) {
          setTrackingConsent(next);
        }
        _notifyConsentChange();
      });
    } catch (err) {
      captureException(err);
    }
  }
}

const DEFAULT_GTM_HOST = 'https://t.waipu.tv';

function loadGTM(containerId: string, host = DEFAULT_GTM_HOST, seed?: Record<string, unknown>) {
  const dl = getOrCreateDataLayer();
  // Seed app-scoped variables before gtm.start so the first tag evaluation already sees them.
  if (seed) {
    dl.push(seed);
  }
  dl.push({ 'gtm.start': Date.now(), event: 'gtm.js' });
  const script = document.createElement('script');
  script.async = true;
  script.id = 'gtm-script';
  script.src = `${host}/gtm.js?id=${containerId}`;
  document.head.appendChild(script);
}

export function getConfig(): TrackingConfig | null {
  return config;
}

/** @internal — test-only reset */
export function _resetInit() {
  config = null;
  cleanupConsentSubscription?.();
  cleanupConsentSubscription = null;
}
